import { Button, ButtonProps } from '@fly/core';
import { NavMenuItemButton } from '@fly/shared';
import { HoverCard, Separator } from '@radix-ui/themes';

export type SubMenuListItem = {
  title: React.ReactNode;
  button?: NavMenuItemButton;
  disabled?: boolean;
  subItems?: SubMenuListItems;
  rootButtonProps?: ButtonProps;
};

export type SubMenuListItems = { key: string; items: SubMenuListItem[] }[];

export type SubMenuListProps = {
  items: SubMenuListItems;
  onNavClick?: () => void;
};

export function SubMenuList({ items, onNavClick }: SubMenuListProps) {
  return (
    <aside className='w-full'>
      {items.map((group, index) => (
        <div key={group.key}>
          <div className='px-fly-3 pt-fly-3 pb-fly-3'>
            {group.items.map((item) =>
              item.subItems ? (
                <ItemButtonWithSubItems
                  key={item.title as string}
                  item={item}
                  onNavClick={onNavClick}
                />
              ) : (
                <ItemButton
                  key={item.title as string}
                  item={item}
                  onNavClick={onNavClick}
                />
              )
            )}
          </div>
          {!!items[index + 1] && (
            <Separator orientation='horizontal' size='4' />
          )}
        </div>
      ))}
    </aside>
  );
}

function ItemButton({
  item,
  onNavClick,
}: {
  item: SubMenuListItem;
  onNavClick?: () => void;
}) {
  return (
    <div className='flex items-center justify-between gap-fly-5'>
      <Button
        size='2'
        variant='ghost'
        color='neutral'
        highContrast
        {...item.rootButtonProps}
        onClick={(e) => {
          item.rootButtonProps?.onClick?.(e);
          onNavClick?.();
        }}
      >
        {item.title}
      </Button>
      {item.button && (
        <Button
          size='2'
          variant='ghost'
          color='accent'
          to={item.button.to}
          onClick={() => {
            item.button?.onClick?.();
            onNavClick?.();
          }}
        >
          {item.button.title}
        </Button>
      )}
    </div>
  );
}

function ItemButtonWithSubItems({
  item,
  onNavClick,
}: {
  item: SubMenuListItem;
  onNavClick?: () => void;
}) {
  return (
    <HoverCard.Root>
      <HoverCard.Trigger>
        <div className='flex items-center justify-between gap-fly-5'>
          <Button
            iconEnd='arrow_chevron_right'
            size='2'
            variant='ghost'
            color='neutral'
            className='w-full'
            highContrast
            {...item.rootButtonProps}
            onClick={(e) => {
              item.rootButtonProps?.onClick?.(e);
            }}
          >
            <span className='w-full text-left'>{item.title}</span>
          </Button>
          {item.button && (
            <Button
              size='2'
              variant='ghost'
              color='accent'
              to={item.button.to}
              onClick={() => {
                item.button?.onClick?.();
                onNavClick?.();
              }}
            >
              {item.button.title}
            </Button>
          )}
        </div>
      </HoverCard.Trigger>
      <HoverCard.Content
        className='p-0 min-w-[236px] bg-(--fly-color-scheme-gray-0)'
        side='left'
        sideOffset={16}
      >
        <SubMenuList items={item.subItems!} onNavClick={onNavClick} />
      </HoverCard.Content>
    </HoverCard.Root>
  );
}
